import React, { useState, useEffect } from 'react';
import { theme } from '../../../theme';
import { useFixedBookings } from '../../../hooks/venue/useFixedBookings';
import { useOwnerComplex } from '../../../hooks/venue/useOwnerComplex';
import FixedBookingCard from '../../../components/venue/owner/FixedBookingCard';
import FixedBookingWeeklyGrid from '../../../components/venue/owner/FixedBookingWeeklyGrid';
import CreateFixedBookingModal from '../../../components/venue/owner/CreateFixedBookingModal';
import {
  FixedBooking,
  FixedBookingStatus,
  CreateFixedBookingRequest,
  UpdateFixedBookingRequest,
  GridVenue,
  DAY_OF_WEEK_LABELS,
} from '../../../types/venue';
import { Plus, RefreshCw, Filter, Calendar, LayoutGrid, List } from 'lucide-react';
import { apiRequest } from '../../../utils/apiUtils';

const FixedBookingsPage: React.FC = () => {
  const {
    fixedBookings,
    loading,
    error,
    fetchFixedBookings,
    createFixedBooking,
    updateFixedBooking,
    pauseFixedBooking,
    resumeFixedBooking,
    cancelFixedBooking,
    activeCount,
    pausedCount,
    totalCount,
  } = useFixedBookings();

  const { complex, fetchComplex } = useOwnerComplex();

  const [venues, setVenues] = useState<GridVenue[]>([]);
  const [viewMode, setViewMode] = useState<'cards' | 'calendar'>('cards');
  const [selectedStatus, setSelectedStatus] = useState<FixedBookingStatus | ''>('');
  const [selectedVenueId, setSelectedVenueId] = useState<string>('');
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [editingFixedBooking, setEditingFixedBooking] = useState<FixedBooking | null>(null);
  const [actionLoading, setActionLoading] = useState<string | null>(null);

  useEffect(() => {
    fetchComplex();
    fetchFixedBookings();
    fetchVenues();
  }, [fetchComplex, fetchFixedBookings]);

  // Re-fetch when filters change
  useEffect(() => {
    const filters: { status?: FixedBookingStatus; venueId?: string } = {};
    if (selectedStatus) filters.status = selectedStatus;
    if (selectedVenueId) filters.venueId = selectedVenueId;
    fetchFixedBookings(filters);
  }, [selectedStatus, selectedVenueId, fetchFixedBookings]);

  const fetchVenues = async () => {
    const response = await apiRequest<GridVenue[]>('/owner/venues');
    if (response.data) {
      setVenues(Array.isArray(response.data) ? response.data : []);
    }
  };

  const handleCreateSubmit = async (
    data: CreateFixedBookingRequest | UpdateFixedBookingRequest,
    isEdit: boolean
  ) => {
    if (isEdit && editingFixedBooking) {
      return updateFixedBooking(editingFixedBooking.id, data as UpdateFixedBookingRequest);
    }
    return createFixedBooking(data as CreateFixedBookingRequest);
  };

  const handlePause = async (id: string) => {
    setActionLoading(id);
    await pauseFixedBooking(id);
    setActionLoading(null);
  };

  const handleResume = async (id: string) => {
    setActionLoading(id);
    await resumeFixedBooking(id);
    setActionLoading(null);
  };

  const handleCancel = async (id: string) => {
    setActionLoading(id);
    await cancelFixedBooking(id, false);
    setActionLoading(null);
  };

  const handleEdit = (fixedBooking: FixedBooking) => {
    setEditingFixedBooking(fixedBooking);
    setIsCreateModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsCreateModalOpen(false);
    setEditingFixedBooking(null);
  };

  // Group fixed bookings by day of week
  const groupedByDay = fixedBookings.reduce(
    (acc, fb) => {
      if (!acc[fb.dayOfWeek]) {
        acc[fb.dayOfWeek] = [];
      }
      acc[fb.dayOfWeek].push(fb);
      return acc;
    },
    {} as Record<number, FixedBooking[]>
  );

  // Sort within each day by start time
  Object.keys(groupedByDay).forEach((day) => {
    groupedByDay[Number(day)].sort((a, b) => a.startTime.localeCompare(b.startTime));
  });

  // Styles
  const containerStyle: React.CSSProperties = {
    minHeight: '100vh',
    backgroundColor: theme.colors.background,
    padding: '32px',
    maxWidth: '1400px',
    margin: '0 auto',
  };

  const headerStyle: React.CSSProperties = {
    marginBottom: '24px',
  };

  const titleRowStyle: React.CSSProperties = {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '8px',
  };

  const titleStyle: React.CSSProperties = {
    fontSize: '2.5rem',
    fontWeight: theme.fonts.weightBold,
    color: theme.colors.text,
    fontFamily: theme.fonts.heading,
    letterSpacing: theme.fonts.letterSpacingWide,
  };

  const subtitleStyle: React.CSSProperties = {
    fontSize: theme.fonts.sizeBody,
    color: theme.colors.textSecondary,
  };

  const headerActionsStyle: React.CSSProperties = {
    display: 'flex',
    gap: '12px',
  };

  const buttonStyle: React.CSSProperties = {
    padding: '12px 24px',
    borderRadius: theme.borderRadius.button,
    fontSize: theme.fonts.sizeBody,
    fontWeight: theme.fonts.weightBold,
    cursor: 'pointer',
    transition: 'all 0.2s',
    border: 'none',
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    backgroundColor: '#D4AF37', // Gold for fixed bookings
    color: '#000',
  };

  const secondaryButtonStyle: React.CSSProperties = {
    ...buttonStyle,
    backgroundColor: theme.colors.surface,
    color: theme.colors.text,
    border: `2px solid ${theme.colors.border}`,
  };

  const statsBarStyle: React.CSSProperties = {
    display: 'flex',
    gap: '24px',
    padding: '16px 24px',
    backgroundColor: theme.colors.surface,
    borderRadius: theme.borderRadius.card,
    border: `1px solid ${theme.colors.border}`,
    marginBottom: '16px',
  };

  const statItemStyle: React.CSSProperties = {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
  };

  const statLabelStyle: React.CSSProperties = {
    fontSize: theme.fonts.sizeSmall,
    color: theme.colors.textSecondary,
  };

  const statValueStyle: React.CSSProperties = {
    fontSize: theme.fonts.sizeBody,
    fontWeight: theme.fonts.weightBold,
    color: theme.colors.text,
  };

  const filtersStyle: React.CSSProperties = {
    display: 'flex',
    gap: '16px',
    padding: '16px 24px',
    backgroundColor: theme.colors.surface,
    borderRadius: theme.borderRadius.card,
    border: `1px solid ${theme.colors.border}`,
    marginBottom: '24px',
    alignItems: 'center',
    flexWrap: 'wrap',
  };

  const filterLabelStyle: React.CSSProperties = {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    fontSize: theme.fonts.sizeSmall,
    color: theme.colors.textSecondary,
  };

  const selectStyle: React.CSSProperties = {
    padding: '8px 16px',
    borderRadius: theme.borderRadius.button,
    border: `1px solid ${theme.colors.border}`,
    backgroundColor: theme.colors.background,
    color: theme.colors.text,
    fontSize: theme.fonts.sizeSmall,
    cursor: 'pointer',
    minWidth: '150px',
  };

  const daySectionStyle: React.CSSProperties = {
    marginBottom: '32px',
  };

  const daySectionHeaderStyle: React.CSSProperties = {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    marginBottom: '16px',
    padding: '12px 16px',
    backgroundColor: '#D4AF3715',
    borderRadius: theme.borderRadius.button,
    borderLeft: `4px solid #D4AF37`,
  };

  const dayTitleStyle: React.CSSProperties = {
    fontSize: theme.fonts.sizeSubtitle,
    fontWeight: theme.fonts.weightBold,
    color: theme.colors.text,
  };

  const dayCountStyle: React.CSSProperties = {
    fontSize: theme.fonts.sizeSmall,
    color: theme.colors.textSecondary,
    marginLeft: 'auto',
  };

  const gridStyle: React.CSSProperties = {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fill, minmax(350px, 1fr))',
    gap: '20px',
  };

  const loadingStyle: React.CSSProperties = {
    textAlign: 'center',
    padding: '64px 32px',
    fontSize: theme.fonts.sizeSubtitle,
    color: theme.colors.textSecondary,
  };

  const errorStyle: React.CSSProperties = {
    textAlign: 'center',
    padding: '32px',
    backgroundColor: theme.colors.surface,
    borderRadius: theme.borderRadius.card,
    border: `2px solid ${theme.colors.danger}`,
    color: theme.colors.danger,
    marginBottom: '24px',
  };

  const emptyStateStyle: React.CSSProperties = {
    textAlign: 'center',
    padding: '64px 32px',
    backgroundColor: theme.colors.surface,
    borderRadius: theme.borderRadius.card,
    border: `1px solid ${theme.colors.border}`,
  };

  const emptyIconStyle: React.CSSProperties = {
    marginBottom: '16px',
    color: theme.colors.textSecondary,
  };

  const emptyTitleStyle: React.CSSProperties = {
    fontSize: theme.fonts.sizeSubtitle,
    fontWeight: theme.fonts.weightBold,
    color: theme.colors.text,
    marginBottom: '8px',
  };

  const emptyMessageStyle: React.CSSProperties = {
    fontSize: theme.fonts.sizeBody,
    color: theme.colors.textSecondary,
    marginBottom: '24px',
  };

  if (loading && fixedBookings.length === 0) {
    return (
      <div style={containerStyle}>
        <div style={loadingStyle}>Cargando turnos fijos...</div>
      </div>
    );
  }

  return (
    <div style={containerStyle}>
      {/* Header */}
      <div style={headerStyle}>
        <div style={titleRowStyle}>
          <div>
            <h1 style={titleStyle}>TURNOS FIJOS</h1>
            <p style={subtitleStyle}>
              {complex?.name || 'Mi Complejo'} - Gestiona reservas recurrentes semanales
            </p>
          </div>
          <div style={headerActionsStyle}>
            <button
              style={secondaryButtonStyle}
              onClick={() => fetchFixedBookings()}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = theme.colors.background;
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = theme.colors.surface;
              }}
            >
              <RefreshCw size={18} />
              Actualizar
            </button>
            <button
              style={buttonStyle}
              onClick={() => setIsCreateModalOpen(true)}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = '#B8952F';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = '#D4AF37';
              }}
            >
              <Plus size={20} />
              Nuevo Turno Fijo
            </button>
          </div>
        </div>
      </div>

      {error && <div style={errorStyle}>{error}</div>}

      {/* Stats Bar */}
      <div style={statsBarStyle}>
        <div style={statItemStyle}>
          <span style={statLabelStyle}>Total:</span>
          <span style={statValueStyle}>{totalCount}</span>
        </div>
        <div style={statItemStyle}>
          <span style={statLabelStyle}>Activos:</span>
          <span style={{ ...statValueStyle, color: theme.colors.success }}>
            {activeCount}
          </span>
        </div>
        <div style={statItemStyle}>
          <span style={statLabelStyle}>Pausados:</span>
          <span style={{ ...statValueStyle, color: theme.colors.warning }}>
            {pausedCount}
          </span>
        </div>
      </div>

      {/* View Toggle */}
      <div style={{
        display: 'flex',
        gap: '4px',
        marginBottom: '16px',
        backgroundColor: theme.colors.surface,
        borderRadius: theme.borderRadius.card,
        border: `1px solid ${theme.colors.border}`,
        padding: '4px',
        width: 'fit-content',
      }}>
        <button
          style={{
            padding: '8px 20px',
            borderRadius: theme.borderRadius.button,
            fontSize: theme.fonts.sizeSmall,
            fontWeight: theme.fonts.weightBold,
            cursor: 'pointer',
            border: 'none',
            display: 'flex',
            alignItems: 'center',
            gap: '6px',
            backgroundColor: viewMode === 'cards' ? '#D4AF37' : 'transparent',
            color: viewMode === 'cards' ? '#000' : theme.colors.textSecondary,
            transition: 'all 0.2s',
          }}
          onClick={() => setViewMode('cards')}
        >
          <List size={16} />
          Cards
        </button>
        <button
          style={{
            padding: '8px 20px',
            borderRadius: theme.borderRadius.button,
            fontSize: theme.fonts.sizeSmall,
            fontWeight: theme.fonts.weightBold,
            cursor: 'pointer',
            border: 'none',
            display: 'flex',
            alignItems: 'center',
            gap: '6px',
            backgroundColor: viewMode === 'calendar' ? '#D4AF37' : 'transparent',
            color: viewMode === 'calendar' ? '#000' : theme.colors.textSecondary,
            transition: 'all 0.2s',
          }}
          onClick={() => setViewMode('calendar')}
        >
          <LayoutGrid size={16} />
          Calendario
        </button>
      </div>

      {/* Filters */}
      <div style={filtersStyle}>
        <div style={filterLabelStyle}>
          <Filter size={16} />
          Filtros:
        </div>
        <select
          style={selectStyle}
          value={selectedStatus}
          onChange={(e) => setSelectedStatus(e.target.value as FixedBookingStatus | '')}
        >
          <option value="">Todos los estados</option>
          <option value="ACTIVE">Activos</option>
          <option value="PAUSED">Pausados</option>
          <option value="CANCELLED">Cancelados</option>
        </select>
        {venues.length > 1 && (
          <select
            style={selectStyle}
            value={selectedVenueId}
            onChange={(e) => setSelectedVenueId(e.target.value)}
          >
            <option value="">Todas las canchas</option>
            {venues.map((v) => (
              <option key={v.id} value={v.id}>
                {v.name}
              </option>
            ))}
          </select>
        )}
      </div>

      {/* Content */}
      {fixedBookings.length === 0 ? (
        <div style={emptyStateStyle}>
          <div style={emptyIconStyle}>
            <Calendar size={64} />
          </div>
          <div style={emptyTitleStyle}>No hay turnos fijos</div>
          <div style={emptyMessageStyle}>
            Crea tu primer turno fijo para reservas recurrentes semanales
          </div>
          <button
            style={buttonStyle}
            onClick={() => setIsCreateModalOpen(true)}
            onMouseEnter={(e) => {
              e.currentTarget.style.backgroundColor = '#B8952F';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = '#D4AF37';
            }}
          >
            <Plus size={20} />
            Crear Turno Fijo
          </button>
        </div>
      ) : viewMode === 'calendar' ? (
        <FixedBookingWeeklyGrid
          fixedBookings={fixedBookings}
          venues={venues}
          selectedVenueId={selectedVenueId || undefined}
          onFixedBookingClick={handleEdit}
        />
      ) : (
        // Group by day of week
        [1, 2, 3, 4, 5, 6, 0].map((dayOfWeek) => {
          const dayBookings = groupedByDay[dayOfWeek] || [];
          if (dayBookings.length === 0) return null;

          return (
            <div key={dayOfWeek} style={daySectionStyle}>
              <div style={daySectionHeaderStyle}>
                <span style={{ fontSize: '24px' }}>🔄</span>
                <span style={dayTitleStyle}>{DAY_OF_WEEK_LABELS[dayOfWeek]}</span>
                <span style={dayCountStyle}>
                  {dayBookings.length} turno{dayBookings.length !== 1 ? 's' : ''}
                </span>
              </div>
              <div style={gridStyle}>
                {dayBookings.map((fb) => (
                  <FixedBookingCard
                    key={fb.id}
                    fixedBooking={fb}
                    onEdit={handleEdit}
                    onPause={handlePause}
                    onResume={handleResume}
                    onCancel={handleCancel}
                    loading={actionLoading === fb.id}
                  />
                ))}
              </div>
            </div>
          );
        })
      )}

      {/* Create/Edit Modal */}
      <CreateFixedBookingModal
        isOpen={isCreateModalOpen}
        onClose={handleCloseModal}
        onSubmit={handleCreateSubmit}
        venues={venues}
        editingFixedBooking={editingFixedBooking}
        existingFixedBookings={fixedBookings}
      />
    </div>
  );
};

export default FixedBookingsPage;
